
import { Component, Vue } from 'vue-property-decorator'
import {
  listPendingGrants, createPendingGrant, assignPendingGrant,
  cancelPendingGrant, sweepPendingGrants, importHubSpotDeals, importHubSpotContacts,
  PendingGrant, HubSpotImportResult, HubSpotContactImportResult
} from '@/api/pendingGrants'
import { listFeatures } from '@/api/entitlements'
import { AdminApi } from '@/wwapi/apis/admin-api'

// Deals and contacts report differently; the dialog shows one table for both.
interface ImportView {
  source: 'contacts' | 'deals'
  seen: number
  summaryText: string
  rows: Array<{ label: string, features: string[], unknown: string[], email: string | null, outcome: string }>
}

// A promise nobody has claimed after this long is the signal that the address
// on the sale is not the one the buyer registered with.
const STALE_DAYS = 14

@Component({ name: 'PendingGrants' })
export default class extends Vue {
  private STALE_DAYS = STALE_DAYS

  private loading = false
  private saving = false
  private sweeping = false
  private importing = false

  private grants: PendingGrant[] = []
  private features: string[] = []

  private query = { status: 'pending', search: '' }

  private createVisible = false
  private assignVisible = false
  private importVisible = false

  private assigning: PendingGrant | null = null
  private assignUserId = ''
  private userOptions: Array<{ userId: string, label: string }> = []
  private userSearchLoading = false

  private hourOptions = [24, 72, 168, 720, 8760]
  // Contacts first: most closed deals in the portal carry no line items.
  private importSource: 'contacts' | 'deals' = 'contacts'
  private importQuery: { sinceHours: number, refs: string } = { sinceHours: 168, refs: '' }
  private importResult: ImportView | null = null

  private form = {
    email: '', featureKey: '', durationType: 'years', durationValue: '1', notes: ''
  }

  private get rules() {
    return {
      email: [
        { required: true, message: this.$t('pendingGrants.emailRequired'), trigger: 'blur' },
        { type: 'email', message: this.$t('pendingGrants.emailInvalid'), trigger: 'blur' }
      ],
      featureKey: [{ required: true, message: this.$t('pendingGrants.featureRequired'), trigger: 'change' }]
    }
  }

  private get stale() {
    return this.grants.filter(g => g.status === 'pending' && g.ageDays >= STALE_DAYS)
  }

  private created() {
    this.fetch()
    this.loadFeatures()
  }

  private async loadFeatures() {
    try {
      const res: any = await listFeatures()
      const body = res?.data?.data ?? res?.data
      const list: any[] = Array.isArray(body) ? body : (body?.features || [])
      this.features = list.map((f: any) => f.featureKey || f.feature_key).filter(Boolean)
    } catch (err) {
      this.features = []
    }
  }

  private async fetch() {
    this.loading = true
    try {
      const res: any = await listPendingGrants({
        status: this.query.status || undefined,
        search: this.query.search || undefined,
        limit: 200
      })
      this.grants = res.data.data.grants || []
    } catch (err: any) {
      this.$message.error(this.msg(err))
    } finally {
      this.loading = false
    }
  }

  private async runSweep() {
    this.sweeping = true
    try {
      const res: any = await sweepPendingGrants()
      const d = res.data.data
      this.$message.success(this.$t('pendingGrants.sweepDone', { checked: d.checked, claimed: d.claimed }) as string)
      this.fetch()
    } catch (err: any) {
      this.$message.error(this.msg(err))
    } finally {
      this.sweeping = false
    }
  }

  private openAssign(row: PendingGrant) {
    this.assigning = row
    this.assignUserId = ''
    this.userOptions = []
    this.assignVisible = true
  }

  private async remoteSearchUsers(q: string) {
    const text = (q || '').trim()
    if (text.length < 2) {
      this.userOptions = []
      return
    }
    this.userSearchLoading = true
    try {
      const adminApi = new AdminApi()
      const res: any = await adminApi.userListPost({
        isPremium: null,
        isAssistant: null,
        loginType: null,
        userType: null,
        text,
        sort: '+id',
        page: 1,
        limit: 20
      } as any)
      const users: any[] = res.data?.data || []
      this.userOptions = users
        .filter(u => u.userId || u.user_id)
        .map(u => {
          const userId = u.userId || u.user_id
          const name = [u.firstName || u.first_name, u.lastName || u.last_name].filter(Boolean).join(' ').trim()
          return { userId, label: name ? `${name} (${u.email})` : u.email }
        })
    } catch (err) {
      this.userOptions = []
    } finally {
      this.userSearchLoading = false
    }
  }

  private async doAssign() {
    if (!this.assigning || !this.assignUserId) return
    this.saving = true
    try {
      await assignPendingGrant(this.assigning.id, this.assignUserId)
      this.$message.success(this.$t('pendingGrants.assigned') as string)
      this.assignVisible = false
      this.fetch()
    } catch (err: any) {
      this.$message.error(this.msg(err))
    } finally {
      this.saving = false
    }
  }

  private doCreate() {
    (this.$refs.createForm as any).validate(async(valid: boolean) => {
      if (!valid) return
      this.saving = true
      try {
        await createPendingGrant({
          email: this.form.email.trim(),
          featureKey: this.form.featureKey,
          durationType: this.form.durationType,
          durationValue: this.form.durationType === 'permanent' ? null : Number(this.form.durationValue),
          source: 'admin',
          notes: this.form.notes || undefined
        })
        this.$message.success(this.$t('pendingGrants.saved') as string)
        this.createVisible = false
        this.form = { email: '', featureKey: '', durationType: 'years', durationValue: '1', notes: '' }
        this.fetch()
      } catch (err: any) {
        this.$message.error(this.msg(err))
      } finally {
        this.saving = false
      }
    })
  }

  private confirmCancel(row: PendingGrant) {
    const claimed = row.status === 'claimed'
    this.$confirm(
      this.$t(claimed ? 'pendingGrants.revokeConfirm' : 'pendingGrants.cancelConfirm', { feature: row.featureKey }) as string,
      this.$t('pendingGrants.confirmTitle') as string,
      { confirmButtonText: this.$t('pendingGrants.continue') as string, cancelButtonText: this.$t('pendingGrants.close') as string, type: 'warning' }
    ).then(async() => {
      try {
        await cancelPendingGrant(row.id, claimed ? 'revoked_by_admin' : 'cancelled_by_admin')
        this.$message.success(this.$t('pendingGrants.cancelled') as string)
        this.fetch()
      } catch (err: any) {
        this.$message.error(this.msg(err))
      }
    }).catch(() => undefined)
  }

  private async runImport(apply: boolean) {
    this.importing = true
    try {
      const refs = this.importQuery.refs.split(/[\s,;]+/).filter(Boolean)
      const sinceHours = refs.length ? undefined : this.importQuery.sinceHours
      let created: number
      if (this.importSource === 'contacts') {
        const res: any = await importHubSpotContacts({ sinceHours, emails: refs.length ? refs : undefined, limit: 200, apply })
        const data: HubSpotContactImportResult = res.data.data
        created = data.summary.grantsCreated
        this.importResult = {
          source: 'contacts',
          seen: data.summary.contactsSeen,
          summaryText: this.$t('pendingGrants.contactImportSummary', data.summary) as string,
          rows: data.contacts.map(c => ({
            label: c.raw || '-', features: c.features, unknown: c.unknownTokens || [], email: c.email, outcome: c.outcome
          }))
        }
      } else {
        const res: any = await importHubSpotDeals({ sinceHours, dealIds: refs.length ? refs : undefined, limit: 100, apply })
        const data: HubSpotImportResult = res.data.data
        created = data.summary.grantsCreated
        this.importResult = {
          source: 'deals',
          seen: data.summary.dealsSeen,
          summaryText: this.$t('pendingGrants.importSummary', data.summary) as string,
          rows: data.deals.map(d => ({
            label: d.dealName, features: d.features, unknown: [], email: d.email, outcome: d.outcome
          }))
        }
      }
      if (apply) {
        this.$message.success(this.$t('pendingGrants.importApplied', { n: created }) as string)
        this.fetch()
      }
    } catch (err: any) {
      this.$message.error(this.msg(err))
    } finally {
      this.importing = false
    }
  }

  private confirmApply() {
    this.$confirm(
      this.$t('pendingGrants.applyConfirm') as string,
      this.$t('pendingGrants.confirmTitle') as string,
      { confirmButtonText: this.$t('pendingGrants.continue') as string, cancelButtonText: this.$t('pendingGrants.close') as string, type: 'warning' }
    ).then(() => this.runImport(true)).catch(() => undefined)
  }

  private durationLabel(row: PendingGrant) {
    if (row.durationType === 'permanent') return this.$t('pendingGrants.permanent') as string
    return `${row.durationValue} ${this.$t('pendingGrants.' + row.durationType)}`
  }

  private statusTag(status: string) {
    if (status === 'claimed') return 'success'
    if (status === 'cancelled') return 'info'
    return 'warning'
  }

  private outcomeTag(outcome: string) {
    if (outcome === 'created') return 'success'
    if (outcome === 'would_create') return 'warning'
    if (outcome.startsWith('error')) return 'danger'
    return 'info'
  }

  private outcomeLabel(outcome: string) {
    const known = [
      'created', 'would_create', 'no_line_items', 'no_features_on_skus', 'no_contact_email',
      'no_email', 'empty', 'no_recognised_features'
    ]
    return known.includes(outcome) ? (this.$t('pendingGrants.outcome_' + outcome) as string) : outcome
  }

  private rowClass({ row }: { row: PendingGrant }) {
    if (row.status === 'pending' && row.ageDays >= STALE_DAYS) return 'row-stale'
    return ''
  }

  private formatDate(v?: string | null) {
    if (!v) return '-'
    const d = new Date(v)
    return isNaN(d.getTime()) ? '-' : d.toLocaleString('tr-TR')
  }

  private msg(err: any) {
    return err?.response?.data?.status?.message || err?.message || (this.$t('pendingGrants.failed') as string)
  }
}
